/* "Create rack R-05" — the details form.
 *
 * The code alone is enough for the backend (name defaults to it), but a store full
 * of racks called R-05, R-06, R-07 is a store where nobody can find anything, and
 * the moment someone knows what the shelf is for is the moment they are standing
 * at it. So the picker asks — once, with the code already filled in — rather than
 * creating a bare record that somebody is supposed to go back and name later.
 *
 * Only the code is required: an operator mid-receipt can still press Create and
 * move on, which is the whole reason inline creation exists.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, InlineAlert, IconCheck, IconX, IconPackage } from "../supplyKit";
import { rackCodeError } from "./rackService";

const fieldInput = {
  width: "100%",
  height: 30,
  padding: "0 8px",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  background: "var(--bg-panel)",
  color: "var(--fg-default)",
  outline: "none",
  boxSizing: "border-box",
};
const fieldLabel = {
  display: "block",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  marginBottom: 4,
};

/* `onCreate({ code, name, description })` must resolve to the created rack.
   `warehouseLabel` is shown, not editable — the rack belongs to the store being
   worked in, and letting it be changed here would only create a way to file a
   shelf under the wrong branch. */
export const RackCreateDialog = ({ initialCode = "", warehouseLabel, onCreate, onCreated, onCancel }) => {
  const [code, setCode] = useState(initialCode);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const nameRef = useRef(null);

  /* Focus the name, not the code: the code arrived with the operator's keystrokes
     and the name is what is actually being asked for. */
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const codeErr = code ? rackCodeError(code) : null;
  const valid = Boolean(String(code).trim()) && !codeErr;

  const submit = () => {
    if (!valid || saving) return;
    setSaving(true);
    setErr(null);
    Promise.resolve(
      onCreate({ code: code.trim(), name: name.trim(), description: description.trim() })
    )
      .then((rack) => {
        setSaving(false);
        if (rack) onCreated?.(rack);
      })
      .catch((e) => {
        setSaving(false);
        setErr(e?.response?.data?.message || e?.message || "Could not create the rack.");
      });
  };

  return createPortal(
    /* z-index above the picker menu (95) that opened this. */
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 120,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel?.();
      }}
    >
      <div onClick={onCancel} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
      <div
        className="cb-animate-in"
        style={{
          position: "relative",
          width: "min(420px, 92vw)",
          background: "var(--bg-canvas)",
          border: "1px solid var(--border-default)",
          borderRadius: 8,
          boxShadow: "0 18px 48px rgba(13,17,27,0.24)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 16px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: "var(--ink-50)",
              color: "var(--ink-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconPackage size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--fg-strong)" }}>New rack</div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 1 }}>
              {warehouseLabel ? `Storage location in ${warehouseLabel}` : "Storage location"}
            </div>
          </div>
          <button
            onClick={onCancel}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              color: "var(--fg-muted)",
              width: 26,
              height: 26,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconX size={15} />
          </button>
        </div>

        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div>
            <label style={fieldLabel}>Code *</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              maxLength={20}
              placeholder="R-05"
              style={{
                ...fieldInput,
                fontFamily: "var(--font-mono)",
                textTransform: "uppercase",
                border: codeErr ? "1px solid var(--red-500)" : fieldInput.border,
              }}
            />
            {codeErr ? (
              <div style={{ fontSize: 10, color: "var(--red-700)", marginTop: 4 }}>{codeErr}</div>
            ) : (
              <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 4 }}>
                What is painted on the shelf · unique in this store
              </div>
            )}
          </div>
          <div>
            <label style={fieldLabel}>Name</label>
            <input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              maxLength={255}
              placeholder="Antibiotics"
              style={fieldInput}
            />
            <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 4 }}>
              What lives here. Left blank, the code is used.
            </div>
          </div>
          <div>
            <label style={fieldLabel}>Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              maxLength={500}
              placeholder="Second aisle, right wall · room temperature"
              style={fieldInput}
            />
          </div>
          {err && <InlineAlert>{err}</InlineAlert>}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            borderTop: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <Button onClick={onCancel}>Cancel</Button>
          <div style={{ flex: 1 }} />
          <Button variant="primary" icon={<IconCheck size={14} />} disabled={!valid || saving} onClick={submit}>
            {saving ? "Creating…" : "Create rack"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RackCreateDialog;
